import axiosInstance from '../helpers/axios';

/** /api/my/* — the customer admin portal, scoped server-side to the caller's company. */

export type TMyProgram = {
  id: string;
  name: string | null;
  number: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  invested: number | null;
  used: number | null;
  remaining: number | null;
  memberCount: number | null;
  renewed: boolean;
};

export type TMyProgramDetail = Omit<TMyProgram, 'memberCount' | 'renewed'> & {
  members: {
    id: string;
    name: string | null;
    email: string | null;
    status: string | null;
    enabled: boolean;
  }[];
};

export type TMyOverview = {
  account: { accountId: string; accountName: string | null };
  programs: TMyProgram[];
  upcomingPrivateEvents: number;
  openTickets: number;
  registrationsThisYear: number;
  evaluations: number;
};

export type TMyEvent = {
  id: string;
  name: string | null;
  status: string | null;
  start: string | null;
  end: string | null;
  instructor: string | null;
  booked: number | null;
  location: string | null;
};

export type TMyEventDetail = TMyEvent & {
  durationHours: number | null;
  cost: number | null;
  seatsAllowed: number | null;
  seatsBooked: number | null;
  attended: number | null;
  videoProvider: string | null;
  privateClassLink: string | null;
  materialsLink: string | null;
  surveyLink: string | null;
  registrations: {
    id: string;
    number: string | null;
    status: string | null;
    name: string | null;
    email: string | null;
  }[];
};

export type TMyRegistration = {
  id: string;
  number: string | null;
  course: string | null;
  category: string | null;
  start: string | null;
  learner: string | null;
  email: string | null;
  status: string | null;
  hours: number | null;
};

export type TMyRegistrations = {
  total: number;
  page: number;
  pageSize: number;
  trainingHours: number;
  byStatus: { status: string; count: number }[];
  popular: { course: string; count: number }[];
  items: TMyRegistration[];
};

export type TMyEvaluations = {
  total: number;
  page: number;
  pageSize: number;
  kpis: {
    courseRating: number | null;
    recommend: number | null;
    instructorRating: number | null;
    learnerKnowledge: number | null;
    courseMaterial: number | null;
    count: number;
  };
  items: {
    id: string;
    course: string | null;
    date: string | null;
    type: string | null;
    attendee: string | null;
    courseRating: number | null;
    recommend: number | null;
    takeaways: string | null;
  }[];
};

export type TMyTicket = {
  id: string;
  number: string | null;
  subject: string | null;
  status: string | null;
  type: string | null;
  created: string | null;
  closed: string | null;
  isClosed: boolean;
  mine: boolean;
  /** The event on the account this ticket is about, when it was linked. */
  eventId: string | null;
  eventName: string | null;
};

export type TMyAttendance = {
  overall: {
    classes: number;
    registrations: number;
    attended: number;
    attendancePct: number | null;
  };
  items: {
    key: string;
    course: string | null;
    start: string | null;
    category: string | null;
    registrations: number;
    attended: number;
    noAttend: number;
    /** attended ÷ seats held; null until the class has started */
    attendancePct: number | null;
  }[];
};

export type TMyCatalogue = {
  categories: string[];
  formats: string[];
  items: {
    id: string;
    name: string | null;
    category: string | null;
    duration: string | null;
  }[];
};

export type RegFilters = {
  status?: string;
  category?: string;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
};
export type EvalFilters = {
  type?: string;
  from?: string;
  to?: string;
  page?: number;
};

/**
 * Staff preview. A manager is always scoped to their own company by the
 * API; an ADMIN may pick a customer account to see exactly what that
 * customer's manager sees. The pick lives in this browser only and is sent
 * as ?accountId= on every /my request — the API refuses it for non-admins.
 */
const PREVIEW_KEY = 'my.previewAccount';
export type TPreviewAccount = { id: string; name: string };

export const getPreviewAccount = (): TPreviewAccount | null => {
  try {
    const raw = localStorage.getItem(PREVIEW_KEY);
    return raw ? (JSON.parse(raw) as TPreviewAccount) : null;
  } catch {
    return null;
  }
};

export const setPreviewAccount = (a: TPreviewAccount | null) => {
  try {
    if (a) localStorage.setItem(PREVIEW_KEY, JSON.stringify(a));
    else localStorage.removeItem(PREVIEW_KEY);
  } catch {
    /* storage unavailable — preview simply does not persist */
  }
};

const clean = (p: Record<string, unknown> = {}) =>
  Object.fromEntries(
    Object.entries({ accountId: getPreviewAccount()?.id, ...p }).filter(
      ([, v]) => v !== '' && v != null,
    ),
  );

export const getMyOverview = async () =>
  (await axiosInstance.get<TMyOverview>('my/overview', { params: clean() }))
    .data;

export const getMyPrograms = async () =>
  (
    await axiosInstance.get<{ items: TMyProgram[] }>('my/programs', {
      params: clean(),
    })
  ).data.items;

export const exportMyPrograms = async () =>
  (
    await axiosInstance.get<Blob>('my/programs/export.csv', {
      params: clean(),
      responseType: 'blob',
    })
  ).data;

/** The utilisation report for one program as CSV (T5.08). */
export const exportMyProgramReport = async (id: string) =>
  (
    await axiosInstance.get<Blob>(`my/programs/${id}/report.csv`, {
      params: clean(),
      responseType: 'blob',
    })
  ).data;

export const getMyProgram = async (id: string) =>
  (
    await axiosInstance.get<TMyProgramDetail>(`my/programs/${id}`, {
      params: clean(),
    })
  ).data;

export const getMyEvents = async (
  when: 'upcoming' | 'past' | 'all',
  q?: string,
) =>
  (
    await axiosInstance.get<{ total: number; items: TMyEvent[] }>('my/events', {
      params: clean({ when, q }),
    })
  ).data;

export const getMyEvent = async (id: string) =>
  (
    await axiosInstance.get<TMyEventDetail>(`my/events/${id}`, {
      params: clean(),
    })
  ).data;

export const getMyRegistrations = async (f: RegFilters) =>
  (
    await axiosInstance.get<TMyRegistrations>('my/registrations', {
      params: clean(f),
    })
  ).data;

export const getMyRegistrationsAttendance = async (f: {
  from?: string;
  to?: string;
}) =>
  (
    await axiosInstance.get<TMyAttendance>('my/registrations/attendance', {
      params: clean(f),
    })
  ).data;

export const exportMyRegistrations = async (f: RegFilters) =>
  (
    await axiosInstance.get<Blob>('my/registrations/export.csv', {
      params: clean(f),
      responseType: 'blob',
    })
  ).data;

export const getMyEvaluations = async (f: EvalFilters) =>
  (
    await axiosInstance.get<TMyEvaluations>('my/evaluations', {
      params: clean(f),
    })
  ).data;

export const exportMyEvaluations = async (f: EvalFilters) =>
  (
    await axiosInstance.get<Blob>('my/evaluations/export.csv', {
      params: clean(f),
      responseType: 'blob',
    })
  ).data;

export const getMyTickets = async () =>
  (
    await axiosInstance.get<{ items: TMyTicket[] }>('my/tickets', {
      params: clean(),
    })
  ).data.items;

export const createMyTicket = async (body: {
  subject: string;
  details: string;
  eventId?: string;
}) =>
  (
    await axiosInstance.post<{ id: string; number: string }>(
      'my/tickets',
      body,
      {
        params: clean(),
      },
    )
  ).data;

export const getMyCatalogue = async (category?: string, q?: string) =>
  (
    await axiosInstance.get<TMyCatalogue>('my/catalogue', {
      params: clean({ category, q }),
    })
  ).data;

export const requestMyCourse = async (body: {
  category: string;
  format: string;
  productId: string;
  date: string;
  time: string;
  notes?: string;
}) =>
  (
    await axiosInstance.post<{ id: string; number: string }>(
      'my/requests',
      body,
      { params: clean() },
    )
  ).data;

/** A date / time change on an event already on the account — lands pre-linked. */
export const requestMyChange = async (body: {
  eventId: string;
  requestedDate?: string;
  requestedTime?: string;
  notes?: string;
}) =>
  (
    await axiosInstance.post<{ id: string; number: string }>(
      'my/requests',
      { kind: 'change', ...body },
      { params: clean() },
    )
  ).data;

// ---------------------------------------------------- self-serve writes

export type TMyContact = {
  id: string;
  name: string | null;
  email: string | null;
  title: string | null;
};

export type TAddPerson = {
  contactId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  waitlist?: boolean;
};

export const searchMyContacts = async (q: string) =>
  (
    await axiosInstance.get<{ items: TMyContact[] }>('my/contacts', {
      params: clean({ q }),
    })
  ).data.items;

export const addMyLearner = async (eventId: string, body: TAddPerson) =>
  (
    await axiosInstance.post<{
      id: string;
      contactId: string;
      status: string;
      seats: number | null;
      booked: number | null;
    }>(`my/events/${eventId}/registrations`, body, { params: clean() })
  ).data;

/** An upcoming, open public class anyone can book a seat on (T7.10). */
export type TMyPublicClass = {
  id: string;
  name: string | null;
  start: string | null;
  end: string | null;
  timeZone: string | null;
  durationHours: number | null;
  category: string | null;
  instructor: string | null;
  location: string | null;
  seats: number | null;
  booked: number;
};

export const getMyPublicClasses = async (q?: string, category?: string) =>
  (
    await axiosInstance.get<{ total: number; items: TMyPublicClass[] }>(
      'my/public-classes',
      { params: clean({ q, category }) },
    )
  ).data;

export type TPublicEnrolment = {
  id: string;
  contactId: string;
  status: string;
  seats: number | null;
  booked: number | null;
  programId: string | null;
  billing: 'program' | 'one-off';
  confirmation: { status: string; to: string } | null;
};

export const enrolMyPublic = async (classId: string, body: TAddPerson) =>
  (
    await axiosInstance.post<TPublicEnrolment>(
      `my/public-classes/${classId}/registrations`,
      body,
      { params: clean() },
    )
  ).data;

export const cancelMyRegistration = async (id: string) =>
  (
    await axiosInstance.post<{ id: string; status: string }>(
      `my/registrations/${id}/cancel`,
      {},
      { params: clean() },
    )
  ).data;

export const addMyMember = async (programId: string, body: TAddPerson) =>
  (
    await axiosInstance.post<{
      memberId: string;
      contactId: string;
      enabled: boolean;
      reEnabled: boolean;
    }>(`my/programs/${programId}/members`, body, { params: clean() })
  ).data;

export const setMyMember = async (memberId: string, enabled: boolean) =>
  (
    await axiosInstance.patch<{ memberId: string; enabled: boolean }>(
      `my/programs/members/${memberId}`,
      { enabled },
      { params: clean() },
    )
  ).data;
